"use client";

import { Loader2, UserPlus } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { registerUser, suggestUsername } from "@/lib/actions/users";

declare global {
    interface Window {
        grecaptcha?: {
            ready: (cb: () => void) => void;
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        };
    }
}

export function RegisterForm({ siteKey = "", inviteToken, inviteData }: {
    siteKey?: string;
    inviteToken?: string;
    inviteData?: { email: string; name?: string } | null;
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [firstName, setFirstName] = useState(() => {
        if (!inviteData?.name) return '';
        const parts = inviteData.name.split(' ');
        return parts[0] || '';
    });
    const [lastName, setLastName] = useState(() => {
        if (!inviteData?.name) return '';
        const parts = inviteData.name.split(' ');
        return parts.slice(1).join(' ') || '';
    });
    const [username, setUsername] = useState('');
    const [usernameTouched, _setUsernameTouched] = useState(false);
    const [_usernameLoading, setUsernameLoading] = useState(false);
    const [_mounted, setMounted] = useState(false);
    const [recaptchaReady, setRecaptchaReady] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setIsLoading(true);
        setError(null);

        const formData = new FormData(event.currentTarget);
        const firstNameValue = firstName.trim();
        const lastNameValue = lastName.trim();
        const email = (formData.get("email") as string) || "";
        const usernameValue = username.trim() || email.split('@')[0] || "user";
        const password = (formData.get("password") as string) || "";
        const confirmPassword = (formData.get("confirmPassword") as string) || "";

        if (password !== confirmPassword) {
            setError("Passwörter stimmen nicht überein");
            setIsLoading(false);
            return;
        }

        let recaptchaToken = '';
        if (siteKey) {
            if (typeof window === 'undefined' || !window.grecaptcha || !recaptchaReady) {
                setError("reCAPTCHA lädt noch. Bitte erneut versuchen.");
                setIsLoading(false);
                return;
            }

            try {
                recaptchaToken = await new Promise<string>((resolve, reject) => {
                    window.grecaptcha?.ready(() => {
                        window.grecaptcha?.execute(siteKey, { action: 'register' })
                            .then(resolve)
                            .catch(reject);
                    });
                });
            } catch (e) {
                // Surface to console so the system-log-client-reporter
                // forwards it to system_logs; user-facing message is set
                // below.
                console.error('ReCAPTCHA failed:', e);
                setError("reCAPTCHA Fehler: Bitte versuchen Sie es erneut.");
                setIsLoading(false);
                return;
            }
        }

        try {
            const result = await registerUser({
                firstName: firstNameValue,
                lastName: lastNameValue,
                email: inviteData?.email || email,
                username: usernameValue,
                password,
                recaptchaToken,
                inviteToken,
            });

            if (!result.success) {
                setError(result.error);
                setIsLoading(false);
                return;
            }

            setSuccess(true);
            setIsLoading(false);
        } catch (e) {
            console.error(e);
            setError("reCAPTCHA Fehler: Bitte versuchen Sie es erneut.");
            setIsLoading(false);
        }
    }

    // Load reCAPTCHA v3 script
    useEffect(() => {
        if (!siteKey) return;
        let cancelled = false;
        let loadTimeout: number | undefined;

        setRecaptchaReady(false);

        const ensureBadgeVisible = () => {
            if (cancelled) return;
            const badge = document.querySelector<HTMLElement>('.grecaptcha-badge');
            if (badge) {
                badge.style.visibility = 'visible';
                badge.style.opacity = '1';
                badge.style.display = '';
                return;
            }
            window.setTimeout(ensureBadgeVisible, 300);
        };

        if (!document.getElementById('recaptcha-script')) {
            const script = document.createElement("script");
            script.id = 'recaptcha-script';
            script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                if (cancelled) return;
                window.grecaptcha?.ready(() => {
                    if (!cancelled) setRecaptchaReady(true);
                });
            };
            script.onerror = () => {
                if (!cancelled) setRecaptchaReady(true);
            };
            document.body.appendChild(script);

            // Timeout: allow registration after 10s even if reCAPTCHA fails to load
            loadTimeout = window.setTimeout(() => {
                if (!cancelled) setRecaptchaReady(true);
            }, 10_000);
        } else {
            window.grecaptcha?.ready(() => {
                if (!cancelled) setRecaptchaReady(true);
            });
        }

        ensureBadgeVisible();

        return () => {
            cancelled = true;
            if (loadTimeout) clearTimeout(loadTimeout);
            document.querySelectorAll('.grecaptcha-badge').forEach((badge) => badge.remove());
            const script = document.getElementById('recaptcha-script');
            if (script) {
                script.remove();
            }
        };
    }, [siteKey]);

    useEffect(() => {
        if (usernameTouched) return;
        const trimmedFirst = firstName.trim();
        const trimmedLast = lastName.trim();
        if (!trimmedFirst && !trimmedLast) {
            setUsername('');
            setUsernameLoading(false);
            return;
        }

        let active = true;
        setUsernameLoading(true);
        const handle = window.setTimeout(() => {
            suggestUsername(trimmedFirst, trimmedLast)
                .then((result) => {
                    if (!active) return;
                    setUsername(result.success ? result.data?.suggestion || '' : '');
                })
                .catch(() => {
                    if (!active) return;
                    setUsername('');
                })
                .finally(() => {
                    if (!active) return;
                    setUsernameLoading(false);
                });
        }, 300);

        return () => {
            active = false;
            window.clearTimeout(handle);
        };
    }, [firstName, lastName, usernameTouched]);

    return (
        <div className="w-full bg-[#0F172A]/80 backdrop-blur-xl border border-slate-800/60 rounded-2xl shadow-2xl shadow-black/40 pt-4 pb-10 px-6 sm:p-12 sm:pt-8">
            <div className="flex flex-col items-center space-y-4 sm:space-y-6 mb-8 sm:mb-10">
                <div className="relative mt-0">
                    <div className="absolute -inset-8 bg-blue-500/5 blur-3xl rounded-full pointer-events-none" />
                    <picture>
                        <img
                            src="/login-logo.png"
                            alt="Zähler-Portal Logo"
                            className="relative w-[180px] h-auto sm:w-[280px]"
                        />
                    </picture>
                </div>
                <div className="text-center space-y-1">
                    <h1 className="text-xl sm:text-2xl font-bold text-white tracking-[0.2em] uppercase">
                        Zähler-Portal
                    </h1>
                    <p className="hidden sm:block text-base text-slate-300 font-medium">
                        {success ? "Konto erstellt" : "Konto erstellen"}
                    </p>
                </div>
            </div>
            <div className="space-y-3">
                {error && (
                    <div className="mb-4 p-3 text-sm text-red-200 bg-red-500/10 rounded-md border border-red-500/20 text-center">
                        {error}
                    </div>
                )}
                        {success ? (
                            <div className="rounded-md border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200 text-center">
                                Registrierung erfolgreich. Bitte prüfen Sie Ihre E-Mails zur Bestätigung.
                            </div>
                        ) : (
                            <form onSubmit={onSubmit} className="space-y-5 sm:space-y-6">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label htmlFor="firstName" className="hidden sm:flex text-sm font-medium text-slate-200 items-center gap-2">Vorname</Label>
                                    <Input
                                        id="firstName"
                                        name="firstName"
                                        placeholder="Vorname"
                                        disabled={isLoading}
                                        required
                                        value={firstName}
                                        onChange={(event) => setFirstName(event.target.value)}
                                        className="h-11 bg-[#0f172a] border-slate-700 text-slate-100 placeholder:text-slate-400 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/10 rounded-md transition-all"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="lastName" className="hidden sm:flex text-sm font-medium text-slate-200 items-center gap-2">Nachname</Label>
                                    <Input
                                        id="lastName"
                                        name="lastName"
                                        placeholder="Nachname"
                                        disabled={isLoading}
                                        required
                                        value={lastName}
                                        onChange={(event) => setLastName(event.target.value)}
                                        className="h-11 bg-[#0f172a] border-slate-700 text-slate-100 placeholder:text-slate-400 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/10 rounded-md transition-all"
                                    />
                                </div>
                                </div>
                                <div className="space-y-1.5">
                                <Label htmlFor="email" className="hidden sm:flex text-sm font-medium text-slate-200 items-center gap-2">E-Mail-Adresse</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="E-Mail-Adresse"
                                    required
                                    disabled={isLoading || !!inviteData?.email}
                                    readOnly={!!inviteData?.email}
                                    defaultValue={inviteData?.email || ''}
                                    className="h-11 bg-[#0f172a] border-slate-700 text-slate-100 placeholder:text-slate-400 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/10 rounded-md transition-all"
                                />
                                {inviteData?.email && (
                                    <p className="text-xs text-blue-300 ml-1">E-Mail aus Einladung vorausgefüllt</p>
                                )}
                            </div>

                                <div className="space-y-1.5">
                                <Label htmlFor="password" className="hidden sm:flex text-sm font-medium text-slate-200 items-center gap-2">Passwort</Label>
                                <PasswordInput
                                    id="password"
                                    name="password"
                                    placeholder="Passwort"
                                    required
                                    minLength={8}
                                    disabled={isLoading}
                                    className="h-11 bg-[#0f172a] border-slate-700 text-slate-100 placeholder:text-slate-400 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/10 rounded-md transition-all"
                                />
                            </div>
                                <div className="space-y-1.5">
                                <Label htmlFor="confirmPassword" className="hidden sm:flex text-sm font-medium text-slate-200 items-center gap-2">Passwort bestätigen</Label>
                                <PasswordInput
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    placeholder="Passwort bestätigen"
                                    required
                                    minLength={8}
                                    disabled={isLoading}
                                    className="h-11 bg-[#0f172a] border-slate-700 text-slate-100 placeholder:text-slate-400 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/10 rounded-md transition-all"
                                />
                            </div>

                                {siteKey && !recaptchaReady && (
                                    <div className="rounded-md border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-xs text-amber-200">
                                        reCAPTCHA wird geladen...
                                    </div>
                                )}

                            <Button
                                type="submit"
                                className="h-11 w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-md transition-all duration-200 shadow-lg shadow-blue-500/30"
                                disabled={isLoading || (!!siteKey && !recaptchaReady)}
                            >
                                {isLoading ? <Loader2 className="mr-3 h-5 w-5 animate-spin" /> : <UserPlus className="mr-3 h-5 w-5" />}
                                Konto erstellen
                            </Button>



                                {siteKey && (
                                    <p className="text-[10px] text-gray-500 text-center px-3 mt-2">
                                        Diese Seite wird durch reCAPTCHA geschützt. Es gelten die
                                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-400 ml-1">Datenschutzerklärung</a> und
                                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-400 ml-1">Nutzungsbedingungen</a> von Google.
                                    </p>
                                )}
                            </form>
                        )}
            </div>

            <div className="mt-8 sm:mt-10 text-center space-y-4">
                <div className="text-slate-400 text-sm">
                    Bereits registriert?{' '}
                    <Link href="/login" className="text-blue-400 shadow-none drop-shadow-none font-bold hover:text-blue-300 transition-all underline underline-offset-4">
                        Jetzt anmelden
                    </Link>
                </div>
            </div>
        </div>
    );
}
